import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckSquare, Package, Receipt, Plus, Trash2, Pencil, Loader2, X,
  Calendar as CalIcon, Clock, AlertCircle, Building2, User, Tag, ChevronRight,
  Brain, Upload, Camera
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PageHeader from "../components/PageHeader";

const inputCls = "bg-[#14101c] border-white/10 text-white placeholder:text-slate-600";

export default function PersonalSpacePage({ defaultTab = "tasks" }) {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const [tab, setTab] = useState(defaultTab);
  const [summary, setSummary] = useState({});
  const [societes, setSocietes] = useState([]);

  useEffect(() => { setTab(defaultTab); }, [defaultTab]);

  useEffect(() => {
    api.get("/personal/summary").then(r => setSummary(r.data)).catch(() => {});
    api.get("/societes").then(r => setSocietes(r.data || [])).catch(() => {});
  }, []);

  const refreshSummary = () => api.get("/personal/summary").then(r => setSummary(r.data)).catch(() => {});

  const isAdmin = user?.role === "tenant_admin" || user?.role === "admin";

  const PAGE_META = {
    tasks: { title: "Mes Taches", icon: CheckSquare, subtitle: "Vos taches personnelles (toutes societes + personnel)" },
    equipment: { title: "Mon Equipement", icon: Package, subtitle: "Catalogue personnel de vos equipements et licences" },
    expenses: { title: "Mes Depenses & Notes de frais", icon: Receipt, subtitle: "Notes de frais avec workflow d'approbation + vue sur vos depenses societe" },
  };
  const meta = PAGE_META[tab] || PAGE_META.tasks;

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="personal-space-page">
      <PageHeader
        icon={<meta.icon />}
        title={meta.title}
        description={meta.subtitle}
        testId="personal-space-header"
      />

      {/* Summary cards always visible */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Taches a faire" value={summary.tasks_pending || 0} icon={CheckSquare} color="#f7c935" testid="stat-tasks-pending" />
        <SummaryCard label="Taches en retard" value={summary.tasks_overdue || 0} icon={AlertCircle} color="#ef4444" testid="stat-tasks-overdue" />
        <SummaryCard label="Equipements" value={summary.equipment_items || 0} icon={Package} color="#8b5cf6" testid="stat-equipment" />
        <SummaryCard label={isAdmin && summary.reports_to_approve > 0 ? "Notes a approuver" : "Mes notes de frais"} value={isAdmin && summary.reports_to_approve > 0 ? summary.reports_to_approve : summary.reports_pending || 0} icon={Receipt} color={isAdmin && summary.reports_to_approve > 0 ? "#ef4444" : "#10b981"} testid="stat-expenses" />
      </div>

      {tab === "tasks" && <TasksTab api={api} societes={societes} onChange={refreshSummary} />}
      {tab === "equipment" && <EquipmentTab api={api} onChange={refreshSummary} />}
      {tab === "expenses" && <ExpensesTab api={api} societes={societes} userRole={user?.role} onChange={refreshSummary} />}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-black text-white leading-none">{value}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

// ─── TASKS TAB ─────────────────────────────────────────────
function TasksTab({ api, societes, onChange }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/personal/tasks"); setTasks(r.data || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: "", description: "", societe_id: "",
      due_date: "", due_time: "", priority: "medium", status: "todo", sync_agenda: true,
    });
    setShowDialog(true);
  };
  const openEdit = (t) => {
    setEditing(t.task_id);
    setForm({ ...t });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.title?.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, societe_id: form.societe_id || null };
      if (editing) await api.put(`/personal/tasks/${editing}`, payload);
      else await api.post("/personal/tasks", payload);
      setShowDialog(false);
      await load();
      onChange?.();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette tache ?")) return;
    try { await api.delete(`/personal/tasks/${id}`); await load(); onChange?.(); } catch {}
  };

  const toggleStatus = async (t) => {
    const ns = t.status === "done" ? "todo" : "done";
    try { await api.put(`/personal/tasks/${t.task_id}`, { status: ns }); await load(); onChange?.(); } catch {}
  };

  const [addToAgendaId, setAddToAgendaId] = useState(null);
  const [agendaDate, setAgendaDate] = useState("");
  const [agendaTime, setAgendaTime] = useState("09:00");

  const applyAddToAgenda = async () => {
    if (!addToAgendaId || !agendaDate) return;
    try {
      await api.put(`/personal/tasks/${addToAgendaId}`, { due_date: agendaDate, due_time: agendaTime, sync_agenda: true });
      setAddToAgendaId(null); setAgendaDate(""); setAgendaTime("09:00");
      await load(); onChange?.();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const grouped = { overdue: [], today: [], upcoming: [], nodate: [], done: [] };
  tasks.forEach(t => {
    if (t.status === "done") grouped.done.push(t);
    else if (!t.due_date) grouped.nodate.push(t);
    else if (t.due_date < today) grouped.overdue.push(t);
    else if (t.due_date === today) grouped.today.push(t);
    else grouped.upcoming.push(t);
  });

  const prio = { low: "#10b981", medium: "#f7c935", high: "#ef4444" };

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <h3 className="text-sm font-bold text-white">{tasks.length} tache{tasks.length > 1 ? "s" : ""}</h3>
        <Button onClick={openCreate} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="create-task-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouvelle tache
        </Button>
      </div>

      {loading && <Loader2 className="w-5 h-5 animate-spin text-[#f7c935] mx-auto my-8" />}

      {!loading && tasks.length === 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-10 text-center">
          <CheckSquare className="w-10 h-10 mx-auto text-slate-600 mb-3" />
          <p className="text-sm text-slate-500">Aucune tache pour l'instant</p>
        </div>
      )}

      {["overdue", "today", "upcoming", "nodate", "done"].map(key => {
        const lst = grouped[key];
        if (!lst.length) return null;
        const titles = { overdue: "En retard", today: "Aujourd'hui", upcoming: "A venir", nodate: "Sans date", done: "Terminees" };
        return (
          <div key={key} data-testid={`tasks-group-${key}`}>
            <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${key === "overdue" ? "text-red-400" : "text-slate-500"}`}>{titles[key]} ({lst.length})</h4>
            <div className="space-y-1.5">
              {lst.map(t => {
                const soc = societes.find(s => s.societe_id === t.societe_id);
                return (
                  <div key={t.task_id} className={`bg-[#14101c] border border-white/5 rounded-lg p-3 flex items-start gap-3 group hover:border-white/10 transition-colors ${t.status === "done" ? "opacity-50" : ""}`} data-testid={`task-item-${t.task_id}`}>
                    <button onClick={() => toggleStatus(t)} className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${t.status === "done" ? "bg-[#f7c935] border-[#f7c935]" : "border-white/20 hover:border-[#f7c935]"}`} data-testid={`task-check-${t.task_id}`}>
                      {t.status === "done" && <span className="text-[#0b0811] font-black text-[10px]">✓</span>}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-semibold text-white ${t.status === "done" ? "line-through" : ""}`}>{t.title}</span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: `${prio[t.priority]}18`, color: prio[t.priority] }}>{t.priority}</span>
                        {t.societe_id ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#461d8a]/20 text-[#8b5cf6] flex items-center gap-1"><Building2 className="w-2.5 h-2.5" /> {soc?.nom || "Societe"}</span>
                        ) : (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/10 text-slate-400 flex items-center gap-1"><User className="w-2.5 h-2.5" /> Personnel</span>
                        )}
                      </div>
                      {t.description && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{t.description}</div>}
                      {t.due_date && (
                        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2">
                          <span className="flex items-center gap-1"><CalIcon className="w-2.5 h-2.5" /> {t.due_date}</span>
                          {t.due_time && <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {t.due_time}</span>}
                          {t.sync_agenda && <span className="text-[#f7c935]">· synchro agenda</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      {!t.due_date && t.status !== "done" && (
                        <button onClick={() => { setAddToAgendaId(t.task_id); setAgendaDate(new Date().toISOString().slice(0,10)); setAgendaTime("09:00"); }} className="p-1.5 rounded hover:bg-[#f7c935]/10" title="Ajouter a l'agenda" data-testid={`task-add-agenda-${t.task_id}`}>
                          <CalIcon className="w-3.5 h-3.5 text-[#f7c935]" />
                        </button>
                      )}
                      <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-white/5" data-testid={`task-edit-${t.task_id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                      <button onClick={() => del(t.task_id)} className="p-1.5 rounded hover:bg-red-500/10" data-testid={`task-delete-${t.task_id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Add to agenda popup for dateless tasks */}
      <Dialog open={!!addToAgendaId} onOpenChange={(v) => !v && setAddToAgendaId(null)}>
        <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-sm" data-testid="add-to-agenda-dialog">
          <DialogHeader><DialogTitle>Ajouter a l'agenda</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Date *</label>
              <Input type="date" value={agendaDate} onChange={e => setAgendaDate(e.target.value)} className={inputCls} data-testid="agenda-date" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Heure</label>
              <Input type="time" value={agendaTime} onChange={e => setAgendaTime(e.target.value)} className={inputCls} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setAddToAgendaId(null)} variant="outline" className="flex-1 border-white/10 text-slate-400">Annuler</Button>
              <Button onClick={applyAddToAgenda} disabled={!agendaDate} className="flex-1 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="agenda-confirm">
                <CalIcon className="w-3.5 h-3.5 mr-1" /> Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-lg" data-testid="task-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifier la tache" : "Nouvelle tache"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Titre *</label>
              <Input value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="task-title" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Description</label>
              <Textarea value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Societe</label>
              <Select value={form.societe_id || "__personal"} onValueChange={v => setForm({ ...form, societe_id: v === "__personal" ? "" : v })}>
                <SelectTrigger className={inputCls} data-testid="task-societe"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  <SelectItem value="__personal"><User className="w-3 h-3 inline mr-1" /> Personnel</SelectItem>
                  {societes.map(s => <SelectItem key={s.societe_id} value={s.societe_id}>{s.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Echeance</label>
                <Input type="date" value={form.due_date || ""} onChange={e => setForm({ ...form, due_date: e.target.value })} className={inputCls} data-testid="task-due-date" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Heure</label>
                <Input type="time" value={form.due_time || ""} onChange={e => setForm({ ...form, due_time: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Priorite</label>
                <Select value={form.priority || "medium"} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    <SelectItem value="low">Faible</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Statut</label>
                <Select value={form.status || "todo"} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    <SelectItem value="todo">A faire</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="done">Terminee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.sync_agenda ?? true} onChange={e => setForm({ ...form, sync_agenda: e.target.checked })} className="accent-[#f7c935]" data-testid="task-sync-agenda" />
              <span>Ajouter automatiquement a mon agenda</span>
            </label>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowDialog(false)} variant="outline" className="flex-1 border-white/10 text-slate-400">Annuler</Button>
              <Button onClick={save} disabled={saving || !form.title?.trim()} className="flex-1 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="task-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── EQUIPMENT TAB ─────────────────────────────────────────
function EquipmentTab({ api, onChange }) {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [catForm, setCatForm] = useState({ label: "", color: "#f7c935" });
  const [itemForm, setItemForm] = useState({});
  const [editingCat, setEditingCat] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const load = async () => {
    const [c, i] = await Promise.all([
      api.get("/personal/equipment/categories"),
      api.get("/personal/equipment/items"),
    ]);
    setCats(c.data || []);
    setItems(i.data || []);
    if (!selectedCat && c.data?.[0]) setSelectedCat(c.data[0].category_id);
  };
  useEffect(() => { load(); }, []);

  const saveCat = async () => {
    if (!catForm.label?.trim()) return;
    try {
      if (editingCat) await api.put(`/personal/equipment/categories/${editingCat}`, catForm);
      else await api.post("/personal/equipment/categories", catForm);
      setShowCatDialog(false); setEditingCat(null);
      await load();
    } catch (e) { console.error(e); }
  };

  const delCat = async (id) => {
    if (!window.confirm("Supprimer cette categorie et tous ses items ?")) return;
    try { await api.delete(`/personal/equipment/categories/${id}`); await load(); onChange?.(); } catch {}
  };

  const saveItem = async () => {
    if (!itemForm.name?.trim() || !itemForm.category_id) return;
    try {
      const payload = { ...itemForm, price: parseFloat(itemForm.price) || 0 };
      if (editingItem) await api.put(`/personal/equipment/items/${editingItem}`, payload);
      else await api.post("/personal/equipment/items", payload);
      setShowItemDialog(false); setEditingItem(null);
      await load();
      onChange?.();
    } catch (e) { console.error(e); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Supprimer cet item ?")) return;
    try { await api.delete(`/personal/equipment/items/${id}`); await load(); onChange?.(); } catch {}
  };

  const filteredItems = selectedCat ? items.filter(i => i.category_id === selectedCat) : items;

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Sidebar categories */}
      <div className="col-span-12 md:col-span-3">
        <div className="flex justify-between mb-2">
          <h4 className="text-xs font-bold text-white uppercase tracking-wider">Mes categories</h4>
          <button onClick={() => { setEditingCat(null); setCatForm({ label: "", color: "#f7c935", icon: "Package" }); setShowCatDialog(true); }} className="text-[#f7c935] hover:text-[#e6b825]" data-testid="create-category-btn">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {cats.length === 0 && <p className="text-xs text-slate-500 italic p-3">Creez votre premiere categorie</p>}
        <div className="space-y-1">
          {cats.map(c => {
            const count = items.filter(i => i.category_id === c.category_id).length;
            const active = selectedCat === c.category_id;
            return (
              <button key={c.category_id} onClick={() => setSelectedCat(c.category_id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center gap-2 transition-colors ${active ? "bg-[#f7c935]/10 border border-[#f7c935]/30" : "bg-[#14101c] border border-white/5 hover:border-white/10"}`}
                data-testid={`category-${c.category_id}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: c.color }}></span>
                <span className="flex-1 truncate text-white">{c.label}</span>
                <span className="text-[9px] text-slate-500">{count}</span>
                {active && (
                  <span onClick={e => { e.stopPropagation(); setEditingCat(c.category_id); setCatForm({ label: c.label, color: c.color, icon: c.icon }); setShowCatDialog(true); }} className="cursor-pointer p-0.5 hover:bg-white/10 rounded">
                    <Pencil className="w-3 h-3 text-slate-400" />
                  </span>
                )}
                {active && (
                  <span onClick={e => { e.stopPropagation(); delCat(c.category_id); }} className="cursor-pointer p-0.5 hover:bg-red-500/10 rounded" data-testid={`category-delete-${c.category_id}`}>
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Items grid */}
      <div className="col-span-12 md:col-span-9">
        <div className="flex justify-between mb-3">
          <h4 className="text-xs font-bold text-white uppercase tracking-wider">
            {selectedCat ? cats.find(c => c.category_id === selectedCat)?.label : "Tous les items"} ({filteredItems.length})
          </h4>
          {selectedCat && (
            <Button onClick={() => { setEditingItem(null); setItemForm({ category_id: selectedCat, name: "", price: 0 }); setShowItemDialog(true); }} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="create-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          )}
        </div>

        {filteredItems.length === 0 && (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-10 text-center">
            <Package className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-sm text-slate-500">{selectedCat ? "Aucun item dans cette categorie" : "Selectionnez une categorie"}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredItems.map(it => {
            const c = cats.find(x => x.category_id === it.category_id);
            return (
              <div key={it.item_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 group" data-testid={`item-${it.item_id}`}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${c?.color || "#f7c935"}18`, color: c?.color || "#f7c935" }}>
                    <Package className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white truncate">{it.name}</div>
                    {c && <div className="text-[10px] text-slate-500">{c.label}</div>}
                  </div>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => { setEditingItem(it.item_id); setItemForm({ ...it }); setShowItemDialog(true); }} className="p-1 rounded hover:bg-white/5" data-testid={`item-edit-${it.item_id}`}><Pencil className="w-3 h-3 text-slate-400" /></button>
                    <button onClick={() => delItem(it.item_id)} className="p-1 rounded hover:bg-red-500/10" data-testid={`item-delete-${it.item_id}`}><Trash2 className="w-3 h-3 text-red-400" /></button>
                  </div>
                </div>
                {it.price > 0 && <div className="text-[11px] text-[#f7c935] font-bold">{it.price} EUR</div>}
                {it.purchase_date && <div className="text-[10px] text-slate-500 mt-1">Achat: {it.purchase_date}</div>}
                {it.warranty_until && <div className="text-[10px] text-slate-500">Garantie: {it.warranty_until}</div>}
                {it.notes && <div className="text-[10px] text-slate-400 mt-2 line-clamp-2">{it.notes}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Dialog */}
      <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
        <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-md" data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editingCat ? "Modifier categorie" : "Nouvelle categorie"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Libelle *</label>
              <Input value={catForm.label || ""} onChange={e => setCatForm({ ...catForm, label: e.target.value })} placeholder="Ex: Materiel bureau, Licences, Vehicules..." className={inputCls} data-testid="category-label" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Couleur</label>
              <div className="flex gap-1.5">
                {["#f7c935", "#8b5cf6", "#10b981", "#ef4444", "#3b82f6", "#ec4899", "#f59e0b"].map(c => (
                  <button key={c} onClick={() => setCatForm({ ...catForm, color: c })} className={`w-7 h-7 rounded-full border-2 ${catForm.color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowCatDialog(false)} variant="outline" className="flex-1 border-white/10 text-slate-400">Annuler</Button>
              <Button onClick={saveCat} disabled={!catForm.label?.trim()} className="flex-1 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="category-save">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-lg" data-testid="item-dialog">
          <DialogHeader><DialogTitle>{editingItem ? "Modifier item" : "Nouvel item"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Nom *</label>
              <Input value={itemForm.name || ""} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} className={inputCls} data-testid="item-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Categorie</label>
              <Select value={itemForm.category_id || ""} onValueChange={v => setItemForm({ ...itemForm, category_id: v })}>
                <SelectTrigger className={inputCls}><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  {cats.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date d'achat</label>
                <Input type="date" value={itemForm.purchase_date || ""} onChange={e => setItemForm({ ...itemForm, purchase_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Garantie jusqu'au</label>
                <Input type="date" value={itemForm.warranty_until || ""} onChange={e => setItemForm({ ...itemForm, warranty_until: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Prix (EUR)</label>
              <Input type="number" min="0" step="0.01" value={itemForm.price ?? 0} onChange={e => setItemForm({ ...itemForm, price: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Notes</label>
              <Textarea value={itemForm.notes || ""} onChange={e => setItemForm({ ...itemForm, notes: e.target.value })} rows={2} className={inputCls} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowItemDialog(false)} variant="outline" className="flex-1 border-white/10 text-slate-400">Annuler</Button>
              <Button onClick={saveItem} disabled={!itemForm.name?.trim() || !itemForm.category_id} className="flex-1 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="item-save">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── EXPENSES TAB (notes de frais + vue societe) ──────────────
function ExpensesTab({ api, societes, userRole, onChange }) {
  const [reports, setReports] = useState([]);
  const [societyExp, setSocietyExp] = useState([]);
  const [pendingForApproval, setPendingForApproval] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const ocrFileRef = useRef(null);

  const isAdmin = userRole === "tenant_admin" || userRole === "admin";

  const handleOcr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/depenses/ocr-extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      const data = r.data || {};
      // Map OCR fields to expense report form
      setForm(f => ({
        ...f,
        libelle: f.libelle || data.element || data.societe || "Ticket / facture",
        montant: parseFloat(data.montant_ttc || data.montant_ht || 0) || f.montant || 0,
        date: data.date || f.date,
        notes: f.notes || (data.societe ? `Fournisseur: ${data.societe}` : "") + (data.facture_ref ? ` - Ref: ${data.facture_ref}` : ""),
        category: data.category ? mapOcrCategory(data.category) : f.category,
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur OCR. Reessayez ou saisissez manuellement.");
    }
    setOcrLoading(false);
    if (ocrFileRef.current) ocrFileRef.current.value = "";
  };

  // Map depenses OCR category to expense-report simple categories
  function mapOcrCategory(c) {
    const m = {
      "tickets_cb": "Transport", "tickets_dirigeant": "Repas", "telecom": "Fournitures",
      "vehicules": "Transport", "marketing": "Autre", "divers": "Autre",
    };
    return m[c] || "Autre";
  }

  const load = async () => {
    setLoading(true);
    try {
      const promises = [
        api.get("/personal/expense-reports"),
        api.get("/personal/expenses"),
      ];
      if (isAdmin) promises.push(api.get("/personal/expense-reports/pending-approval"));
      const results = await Promise.all(promises.map(p => p.catch(() => ({ data: [] }))));
      setReports(results[0].data || []);
      setSocietyExp(results[1].data || []);
      if (isAdmin) setPendingForApproval(results[2].data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      date: new Date().toISOString().slice(0, 10),
      libelle: "", montant: 0, category: "Transport", notes: "", societe_id: "", justificatif_url: "",
    });
    setShowDialog(true);
  };

  const openEdit = (r) => {
    setEditingId(r.report_id);
    setForm({ ...r });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.libelle?.trim() || !form.montant || form.montant <= 0) return;
    setSaving(true);
    try {
      const payload = { ...form, montant: parseFloat(form.montant), societe_id: form.societe_id || null };
      if (editingId) await api.put(`/personal/expense-reports/${editingId}`, payload);
      else await api.post("/personal/expense-reports", payload);
      setShowDialog(false);
      await load();
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette note de frais ?")) return;
    try { await api.delete(`/personal/expense-reports/${id}`); await load(); onChange?.(); } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const approve = async (id) => {
    try { await api.put(`/personal/expense-reports/${id}/approve`); await load(); onChange?.(); } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };
  const reject = async () => {
    if (!rejectingId) return;
    try {
      await api.put(`/personal/expense-reports/${rejectingId}/reject`, { reason: rejectReason });
      setRejectingId(null); setRejectReason("");
      await load();
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };
  const reimburse = async (id) => {
    try { await api.put(`/personal/expense-reports/${id}/mark-reimbursed`); await load(); onChange?.(); } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#f7c935] mx-auto my-8" />;

  const StatusBadge = ({ status }) => {
    const colors = {
      pending: { bg: "#f7c93518", fg: "#f7c935", label: "En attente" },
      approved: { bg: "#10b98118", fg: "#10b981", label: "Approuvee" },
      reimbursed: { bg: "#3b82f618", fg: "#3b82f6", label: "Remboursee" },
      rejected: { bg: "#ef444418", fg: "#ef4444", label: "Refusee" },
    };
    const c = colors[status] || colors.pending;
    return <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: c.bg, color: c.fg }}>{c.label}</span>;
  };

  const totalPending = reports.filter(r => r.status === "pending").reduce((s, r) => s + (r.montant || 0), 0);
  const totalApproved = reports.filter(r => r.status === "approved").reduce((s, r) => s + (r.montant || 0), 0);

  return (
    <div className="space-y-6">
      {/* Admin: pending approvals section */}
      {isAdmin && pendingForApproval.length > 0 && (
        <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-xl p-4" data-testid="pending-approvals-section">
          <h3 className="text-sm font-bold text-[#f7c935] mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> A approuver ({pendingForApproval.length})
          </h3>
          <div className="space-y-1.5">
            {pendingForApproval.map(r => (
              <div key={r.report_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3 flex items-center gap-3" data-testid={`pending-approval-${r.report_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{r.libelle}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#461d8a]/20 text-[#8b5cf6]">{r.category}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex gap-2 items-center flex-wrap">
                    <span>{r.submitter?.name || r.submitter?.email}</span>
                    <span>· {r.date}</span>
                    {r.notes && <span>· {r.notes.substring(0, 50)}</span>}
                  </div>
                </div>
                <div className="text-lg font-black text-[#f7c935]">{r.montant} EUR</div>
                <div className="flex gap-1">
                  <Button onClick={() => approve(r.report_id)} className="bg-green-500/15 text-green-400 hover:bg-green-500/25 h-8 text-xs" data-testid={`approve-${r.report_id}`}>Approuver</Button>
                  <Button onClick={() => { setRejectingId(r.report_id); setRejectReason(""); }} className="bg-red-500/15 text-red-400 hover:bg-red-500/25 h-8 text-xs" data-testid={`reject-${r.report_id}`}>Refuser</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My expense reports */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-white">Mes notes de frais ({reports.length})</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Depenses personnelles a se faire rembourser</p>
          </div>
          <Button onClick={openCreate} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="create-expense-report-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle note
          </Button>
        </div>

        {reports.length === 0 && (
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-10 text-center">
            <Receipt className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-sm text-slate-500">Aucune note de frais. Creez-en une pour demander un remboursement.</p>
          </div>
        )}

        {reports.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-[#14101c] border border-white/5 rounded-lg p-3" data-testid="total-pending">
                <div className="text-[10px] text-slate-500 uppercase">En attente</div>
                <div className="text-xl font-black text-[#f7c935]">{totalPending.toFixed(2)} EUR</div>
              </div>
              <div className="bg-[#14101c] border border-white/5 rounded-lg p-3" data-testid="total-approved">
                <div className="text-[10px] text-slate-500 uppercase">A rembourser (approuvees)</div>
                <div className="text-xl font-black text-green-400">{totalApproved.toFixed(2)} EUR</div>
              </div>
            </div>
            <div className="space-y-1.5">
              {reports.map(r => (
                <div key={r.report_id} className="bg-[#14101c] border border-white/5 rounded-lg p-3 flex items-center gap-3 group" data-testid={`expense-report-${r.report_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{r.libelle}</span>
                      <StatusBadge status={r.status} />
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#461d8a]/20 text-[#8b5cf6]">{r.category}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex gap-2 items-center flex-wrap">
                      <span>{r.date}</span>
                      {r.rejection_reason && <span className="text-red-400">· Refus: {r.rejection_reason}</span>}
                      {r.reimbursed_at && <span className="text-blue-400">· Rembourse le {r.reimbursed_at.slice(0, 10)}</span>}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-[#f7c935]">{r.montant} EUR</div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {r.status === "pending" && (
                      <>
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-white/5" data-testid={`edit-report-${r.report_id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                        <button onClick={() => del(r.report_id)} className="p-1.5 rounded hover:bg-red-500/10" data-testid={`delete-report-${r.report_id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                      </>
                    )}
                    {isAdmin && r.status === "approved" && (
                      <Button onClick={() => reimburse(r.report_id)} className="bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 h-7 text-[10px]" data-testid={`reimburse-${r.report_id}`}>Marquer remboursee</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Society expenses (filtered to me) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-white">Mes depenses societe ({societyExp.length})</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Depenses enregistrees dans les modules societe, filtrees sur moi</p>
          </div>
          <Button onClick={() => window.location.href = "/admin/depenses"} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="go-to-depenses">
            Module Depenses <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
        <div className="space-y-1.5">
          {societyExp.slice(0, 20).map(e => {
            const soc = societes.find(s => s.societe_id === e.societe_id);
            return (
              <div key={e.depense_id || e._id} className="bg-[#14101c] border border-white/5 rounded-lg p-3 flex items-center gap-3 opacity-90" data-testid={`society-expense-${e.depense_id}`}>
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{e.libelle || e.description || "Depense"}</div>
                  <div className="text-[10px] text-slate-500 flex gap-2 items-center">
                    {e.date && <span>{e.date}</span>}
                    {e.fournisseur && <span>· {e.fournisseur}</span>}
                    {soc && <span>· {soc.nom}</span>}
                  </div>
                </div>
                <div className="text-sm font-bold text-[#f7c935]">{e.montant} EUR</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create/Edit expense report dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-lg" data-testid="expense-report-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Modifier la note de frais" : "Nouvelle note de frais"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            {/* OCR section */}
            {!editingId && (
              <div className="bg-gradient-to-br from-[#f7c935]/10 to-[#461d8a]/10 border border-[#f7c935]/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-4 h-4 text-[#f7c935]" />
                  <span className="text-xs font-bold text-[#f7c935] uppercase tracking-wider">Reconnaissance IA du ticket</span>
                </div>
                <input
                  ref={ocrFileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleOcr}
                  className="hidden"
                  data-testid="ocr-file-input"
                />
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleOcr}
                  className="hidden"
                  id="ocr-camera-input"
                  data-testid="ocr-camera-input"
                />
                <div className="flex gap-2">
                  <Button type="button" onClick={() => ocrFileRef.current?.click()} disabled={ocrLoading}
                    className="flex-1 bg-[#f7c935]/15 text-[#f7c935] hover:bg-[#f7c935]/25 border border-[#f7c935]/30 font-medium h-8 text-xs" data-testid="ocr-upload-btn">
                    {ocrLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Photo / PDF
                  </Button>
                  <Button type="button" onClick={() => document.getElementById("ocr-camera-input")?.click()} disabled={ocrLoading}
                    className="flex-1 bg-[#461d8a]/15 text-[#8b5cf6] hover:bg-[#461d8a]/25 border border-[#461d8a]/30 font-medium h-8 text-xs" data-testid="ocr-camera-btn">
                    <Camera className="w-3.5 h-3.5 mr-1" /> Prendre photo
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1.5 italic">Importez un ticket/facture et l'IA prerempli les champs (montant, date, fournisseur).</p>
              </div>
            )}

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Libelle *</label>
              <Input value={form.libelle || ""} onChange={e => setForm({ ...form, libelle: e.target.value })} placeholder="Ex: Repas client, essence..." className={inputCls} data-testid="report-libelle" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date</label>
                <Input type="date" value={form.date || ""} onChange={e => setForm({ ...form, date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Montant (EUR) *</label>
                <Input type="number" min="0" step="0.01" value={form.montant ?? 0} onChange={e => setForm({ ...form, montant: e.target.value })} className={inputCls} data-testid="report-montant" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Categorie</label>
                <Select value={form.category || "Transport"} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    {["Transport", "Repas", "Hebergement", "Fournitures", "Materiel", "Formation", "Autre"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Societe imputation</label>
                <Select value={form.societe_id || "__none"} onValueChange={v => setForm({ ...form, societe_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    <SelectItem value="__none">Non specifiee</SelectItem>
                    {societes.map(s => <SelectItem key={s.societe_id} value={s.societe_id}>{s.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">URL justificatif (photo/PDF)</label>
              <Input value={form.justificatif_url || ""} onChange={e => setForm({ ...form, justificatif_url: e.target.value })} placeholder="https://..." className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Notes</label>
              <Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setShowDialog(false)} variant="outline" className="flex-1 border-white/10 text-slate-400">Annuler</Button>
              <Button onClick={save} disabled={saving || !form.libelle?.trim() || !(form.montant > 0)} className="flex-1 bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="report-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject reason modal */}
      <Dialog open={!!rejectingId} onOpenChange={(v) => !v && setRejectingId(null)}>
        <DialogContent className="bg-[#0b0811] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Refuser la note de frais</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Motif du refus (optionnel)" rows={3} className={inputCls} data-testid="reject-reason" />
            <div className="flex gap-2">
              <Button onClick={() => setRejectingId(null)} variant="outline" className="flex-1 border-white/10 text-slate-400">Annuler</Button>
              <Button onClick={reject} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="reject-confirm">Refuser</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
